import { useId, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import type { UseAuthResult } from '../hooks/useAuth.ts';
import { useI18n } from '../i18n/index.ts';

export type LoginDialogProps = {
  auth: UseAuthResult;
  open: boolean;
  onClose: () => void;
};

/**
 * Membership email / code login.
 *
 * iOS long-press paste notes (why it broke before):
 * - `autocomplete="one-time-code"` makes Safari treat the field as SMS OTP.
 * - A scrollable ancestor (`overflow: auto` on the panel) cancels the callout
 *   when the finger moves ~1px during the long-press.
 * - `touch-action: manipulation` on the input can also block the callout.
 * - Living under the player’s visualViewport-pinned `overflow: hidden` shell
 *   caused focus loss; this dialog is portaled to `document.body` instead.
 */
export function LoginDialog({ auth, open, onClose }: LoginDialogProps) {
  const { t } = useI18n();
  const emailId = useId();
  const codeId = useId();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');

  if (!open) return null;

  async function onSend(e: FormEvent) {
    e.preventDefault();
    await auth.sendCode(email);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const outcome = await auth.submitCode(code);
    // Only close on active membership so inactive / session-expired status stays visible.
    if (outcome === 'active') onClose();
  }

  return createPortal(
    <div className="login-dialog" data-testid="login-dialog" role="dialog">
      <div className="login-dialog__panel">
        <header className="login-dialog__header">
          <h2>{t('signIn')}</h2>
          <button
            type="button"
            className="login-dialog__close"
            onClick={onClose}
            aria-label={t('settingsClose')}
          >
            {t('settingsClose')}
          </button>
        </header>

        {auth.userId ? (
          <div className="login-dialog__session" data-testid="login-session">
            <p data-testid="login-status">
              <span className="login-dialog__status-prefix">{t('memberStatusPrefix')}</span>
              {' '}
              <span className="login-dialog__status-value">{auth.statusLabel}</span>
            </p>
            <button
              type="button"
              className="login-dialog__logout"
              data-testid="logout-btn"
              onClick={() => void auth.logout()}
            >
              {t('loginLogout')}
            </button>
          </div>
        ) : (
          <>
            <p className="login-dialog__web-diff" data-testid="login-web-diff">
              {t('loginWebDiff')}
            </p>

            <form className="login-dialog__form" onSubmit={(e) => void onSend(e)}>
              <h3>{t('loginEmailTitle')}</h3>
              <p className="login-dialog__desc">{t('loginEmailDesc')}</p>
              <label htmlFor={emailId}>{t('loginEmailLabel')}</label>
              <input
                id={emailId}
                name="email"
                data-testid="login-email"
                className="login-dialog__field"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={auth.busy}
              />
              <button type="submit" disabled={auth.busy || !email.trim()} data-testid="send-code-btn">
                {auth.busyKind === 'send' ? t('loginSending') : t('loginSendCode')}
              </button>
            </form>

            <form className="login-dialog__form" onSubmit={(e) => void onSubmit(e)}>
              <label htmlFor={codeId}>{t('loginCodeLabel')}</label>
              {/*
                Extension parity: type=text + spellcheck=false.
                Do NOT use autocomplete=one-time-code — UUID membership codes are not SMS OTPs,
                and that attribute makes iOS hijack the field / break long-press paste.
              */}
              <input
                id={codeId}
                name="code"
                data-testid="login-code"
                className="login-dialog__field"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={auth.busy}
              />
              <button type="submit" disabled={auth.busy || !code.trim()} data-testid="submit-code-btn">
                {auth.busyKind === 'submit' ? t('loginSubmitting') : t('loginSubmit')}
              </button>
            </form>
          </>
        )}

        {auth.message ? (
          <p
            className={`login-dialog__msg${auth.messageKind === 'error' ? ' login-dialog__msg--error' : ' login-dialog__msg--ok'}`}
            role={auth.messageKind === 'error' ? 'alert' : 'status'}
            data-testid="login-message"
          >
            {auth.message}
          </p>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
